import { useEffect, useRef, useState } from 'react';
import { copyText } from '../lib/clipboard';
import styles from './CopyButton.module.css';

interface CopyButtonProps {
  /** The text to copy (a hex string like "#0a84ff"). */
  value: string;
  /** Extra class for positioning within a parent (e.g. a swatch corner). */
  className?: string;
}

function CopyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5V4.5A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A compact copy-to-clipboard button. Inherits text color so it stays legible
 *  on any swatch background. Shows a transient check on success. */
export function CopyButton({ value, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // never trigger an enclosing swatch's selection
    const ok = await copyText(value);
    setCopied(ok);
    if (timer.current) clearTimeout(timer.current);
    if (ok) timer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className={`${styles.copy}${className ? ` ${className}` : ''}`}
      aria-label={copied ? `Copied ${value}` : `Copy ${value}`}
      title={copied ? 'Copied' : `Copy ${value}`}
      onClick={handleCopy}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}
